import React, { useState } from 'react';
import { Grid, Box, Tooltip } from '@mui/material';
import { FormInput } from '~/components/FormComponents/FormInput';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox';
import Pencil from '~/components/icons/Pencil.tsx';
import { FormAddressModal } from '~/modules/identification/components/ModalAddress/FormAddressModal';
import { AddressTypes } from '../../forms/GeneralInfo/types';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { styles } from '../../forms/GeneralInfo/styles';
import AddressCodeLabel from '../../forms/GeneralInfo/components/AddressCodeLabel';
import { getInfoClassifierCommunities } from '../../forms/GeneralInfo/utils';
import { defaultAddress } from '../../constants';
import { TAddress } from '../../types';
import { getAddressHeaders } from '../../utils';
import FormGridItem from '~/components/FormComponents/FormGridItem';

interface IAddressInfoProps {
  showAddressCode?: boolean;
  section?: string;
  isPersonAddress?: boolean;
  isDisabledCountry?: boolean;
  informer?: string;
}

export const AddressInfo = React.memo(
  ({
    showAddressCode,
    section,
    isPersonAddress = false,
    isDisabledCountry,
    informer,
  }: IAddressInfoProps) => {
    const [selectedAddressType, setSelectedAddressType] = useState<AddressTypes | null>(null);
    const { control, setValue, trigger } = useFormContext();

    const addressPath = section ? section + '.address' : 'address';
    const permanentAddressLabel = isPersonAddress
      ? 'Адреса фактичного місця проживання'
      : 'Адреса фактичного місцезнаходження';

    const coincidencesLabel = isPersonAddress
      ? 'Адреса фактичного місця проживання збігається з адресою місця реєстрації'
      : 'Адреса фактичного місцезнаходження збігається з адресою місця реєстрації';

    const [registrationAddressText, permanentAddressText, registrationAddress, coincidences] =
      useWatch({
        control,
        name: [
          `${addressPath}.registrationAddress.address`,
          `${addressPath}.permanentAddress.address`,
          `${addressPath}.registrationAddress`,
          `${addressPath}.coincidences`,
        ],
      });
    const [showRegistrationAddressTooltip, setShowRegistrationAddressTooltip] = useState(false);
    const [showPermanentAddressTooltip, setShowPermanentAddressTooltip] = useState(false);

    const handleAddressClick = (addressType: AddressTypes) => {
      setSelectedAddressType(addressType);
      setShowPermanentAddressTooltip(false);
      setShowRegistrationAddressTooltip(false);
    };

    const handleCoincidencesChange = (isChecked: boolean) => {
      setValue(`${addressPath}.permanentAddress`, isChecked ? registrationAddress : defaultAddress);
      trigger(`${addressPath}.permanentAddress`);
    };

    const handleCityIdChange = async (newCityId: number, fieldName: string) => {
      if (newCityId === -1) {
        setValue(fieldName as Path<{ address: TAddress }>, null);
      } else {
        const code = await getInfoClassifierCommunities(newCityId);
        setValue(fieldName as Path<{ address: TAddress }>, code.natClassif);
      }
    };

    const isCountrySelectorDisabled =
      isDisabledCountry && selectedAddressType === AddressTypes.registrationAddress;

    return (
      <Grid size={showAddressCode ? 12 : { xs: 12, sm: 12, md: 6 }} container spacing={1}>
        <Grid size={12} container spacing={3}>
          <Grid
            size={showAddressCode ? { xs: 12, sm: 12, md: 6 } : 12}
            sx={{ alignItems: 'center', display: 'flex', gap: 2 }}
          >
            <Tooltip
              title={registrationAddressText}
              placement="bottom-start"
              disableHoverListener={!!selectedAddressType}
              disableFocusListener
              disableTouchListener
              open={showRegistrationAddressTooltip}
              onOpen={() => setShowRegistrationAddressTooltip(true)}
              onClose={() => setShowRegistrationAddressTooltip(false)}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -24],
                      },
                    },
                  ],
                },
              }}
            >
              <Box component="span" sx={{ width: '100%' }}>
                <FormInput
                  name={`${addressPath}.registrationAddress.address`}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                      'data-testid': 'registrationAddress',
                      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        if (e.target.value) {
                          e.target.setSelectionRange(0, 0);
                        }
                      },
                      onClick: () => {
                        handleAddressClick(AddressTypes.registrationAddress);
                      },
                    },
                  }}
                  label="Адреса місця реєстрації"
                  placeholder="Введіть адресу місця реєстрації"
                  fullWidth
                  sx={styles.addressInput}
                />
              </Box>
            </Tooltip>
            <Box sx={{ mt: 2 }}>
              <Pencil
                testId="registrationAddress.pencil"
                onClick={() => {
                  handleAddressClick(AddressTypes.registrationAddress);
                }}
              />
            </Box>
          </Grid>
          {showAddressCode && (
            <Grid size={showAddressCode ? { xs: 12, sm: 12, md: 6 } : 12}>
              <FormInput
                name={`${addressPath}.registrationAddress.codeAddress`}
                disabled
                fullWidth
                placeholder="Введіть код адреси місця реєстрації"
                label={
                  <AddressCodeLabel
                    label="Код адреси місця реєстрації"
                    tooltipTitle="Код адреси місця реєстрації, зазначеного в ЄДР(в реєстраційних документах),
                      згідно кодифікатору адміністративно-територіальних одиниць та територій
                      територіальних громад (зазначається четвертий рівень у відповідності наказу
                      Міністерства розвитку громад та територій України від 26.11.2020 № 290)"
                  />
                }
                sx={{ '.MuiFormLabel-root': { overflow: 'visible' } }}
              />
            </Grid>
          )}
        </Grid>
        <Grid size={12}>
          <FormCheckbox
            name={`${addressPath}.coincidences`}
            label={coincidencesLabel}
            onChange={handleCoincidencesChange}
          />
        </Grid>
        <Grid size={12} container spacing={3}>
          <Grid
            size={showAddressCode ? { xs: 12, sm: 12, md: 6 } : 12}
            sx={{ alignItems: 'center', display: 'flex', gap: 2 }}
          >
            <Tooltip
              title={permanentAddressText}
              placement="bottom-start"
              open={showPermanentAddressTooltip}
              disableFocusListener
              disableTouchListener
              onOpen={() => setShowPermanentAddressTooltip(true)}
              onClose={() => setShowPermanentAddressTooltip(false)}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -24],
                      },
                    },
                  ],
                },
              }}
            >
              <Box component="span" sx={{ width: '100%' }}>
                <FormInput
                  name={`${addressPath}.permanentAddress.address`}
                  fullWidth
                  disabled={coincidences}
                  label={permanentAddressLabel}
                  placeholder="Введіть адресу фактичного місцезнаходження"
                  sx={styles.addressInput}
                  slotProps={{
                    htmlInput: {
                      readOnly: true,
                      'data-testid': 'permanentAddress',
                      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                        if (e.target.value) {
                          e.target.setSelectionRange(0, 0);
                        }
                      },
                      onClick: () => {
                        handleAddressClick(AddressTypes.permanentAddress);
                      },
                    },
                  }}
                />
              </Box>
            </Tooltip>
            <Box sx={{ mt: 2 }}>
              <Pencil
                testId="permanentAddress.pencil"
                isDisabled={coincidences}
                onClick={() => {
                  handleAddressClick(AddressTypes.permanentAddress);
                }}
              />
            </Box>
          </Grid>

          {showAddressCode && (
            <FormGridItem>
              <FormInput
                name={`${addressPath}.permanentAddress.codeAddress`}
                disabled
                label={
                  <AddressCodeLabel
                    label="Код адреси фактичного місцезнаходження"
                    tooltipTitle="Код адреси фактичного місцезнаходження, згідно кодифікатору
                      адміністративно-територіальних одиниць та територій територіальних громад
                      (зазначається четвертий рівень у відповідності наказу Міністерства розвитку
                      громад та територій України від 26.11.2020 № 290)"
                  />
                }
                placeholder="Введіть код адреси фактичного місця проживання"
                fullWidth
              />
            </FormGridItem>
          )}
        </Grid>
        {!!selectedAddressType && (
          <FormAddressModal
            isModalOpened={!!selectedAddressType}
            onClose={() => setSelectedAddressType(null)}
            addressType={selectedAddressType}
            header={getAddressHeaders(isPersonAddress)[selectedAddressType as AddressTypes]}
            onCityIdChange={handleCityIdChange}
            shouldUpdateAddressCode={showAddressCode}
            section={section}
            isCountrySelectorDisabled={isCountrySelectorDisabled}
            informer={informer}
          />
        )}
      </Grid>
    );
  }
);
